import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  Cloud,
  Copy,
  FileJson,
  KeyRound,
  Lock,
  Pencil,
  Play,
  Plus,
  Save,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Field, Input, Select, Textarea } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { Modal } from "../components/ui/Modal";
import { useAuth } from "../lib/auth";
import {
  ApiError,
  createEndpoint,
  deleteEndpoint,
  designEndpoint,
  listEndpoints,
  listWorkflows,
  mockPreview,
  openApiUrl,
  updateEndpoint,
} from "../lib/api";
import type {
  ApiEndpoint,
  ApiEndpointDetail,
  ApiEndpointSave,
  AuthType,
  Workflow,
} from "../lib/api";
import { cn } from "../lib/cn";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const AUTH_LABELS: Record<AuthType, string> = {
  none: "No auth",
  basic: "Basic",
  client_credentials: "Client credentials",
  jwt: "JWT",
};
const methodTone: Record<string, string> = {
  GET: "bg-emerald-100 text-emerald-700",
  POST: "bg-brand-100 text-brand-700",
  PUT: "bg-amber-100 text-amber-700",
  PATCH: "bg-amber-100 text-amber-700",
  DELETE: "bg-rose-100 text-rose-700",
};

function pretty(v: unknown): string {
  return v ? JSON.stringify(v, null, 2) : "";
}
function parseOrNull(text: string): { value: Record<string, unknown> | null; error: string | null } {
  const t = text.trim();
  if (!t) return { value: null, error: null };
  try {
    return { value: JSON.parse(t), error: null };
  } catch (e) {
    return { value: null, error: (e as Error).message };
  }
}

function Copyable({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="group inline-flex max-w-full items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-left font-mono text-xs text-slate-100"
    >
      <span className="truncate">{value}</span>
      {copied ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-white" />}
    </button>
  );
}

export default function ApiDesigner() {
  const { currentTenant } = useAuth();
  const tenant = currentTenant?.slug ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<ApiEndpoint[] | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ApiEndpoint | "new" | null>(null);

  function reload() {
    if (!tenant) return;
    listEndpoints(tenant).then(setRows).catch(() => setError("Couldn't load your APIs."));
  }

  useEffect(() => {
    if (!tenant) return;
    reload();
    listWorkflows(tenant).then(setWorkflows).catch(() => setWorkflows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  // Opened from the console's "Edit design".
  const editSlug = (location.state as { edit?: string } | null)?.edit;
  useEffect(() => {
    if (editSlug && rows) {
      const found = rows.find((r) => r.slug === editSlug);
      if (found) setEditing(found);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSlug, rows]);

  return (
    <>
      <PageHeader
        title="API Designer"
        description="Design HTTP APIs with an OpenAPI schema, mock the responses, or back them with a workflow — then secure and share them."
        actions={
          <div className="flex items-center gap-2">
            {rows && rows.length > 0 && (
              <a
                href={`${window.location.origin}${openApiUrl(tenant)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                <FileJson className="h-4 w-4" /> OpenAPI
              </a>
            )}
            <Button onClick={() => setEditing("new")} disabled={!tenant}>
              <Plus className="h-4 w-4" /> New API endpoint
            </Button>
          </div>
        }
      />

      {rows === null && !error ? (
        <div className="flex justify-center py-20"><Spinner className="h-8 w-8 text-brand-500" /></div>
      ) : error ? (
        <Card className="px-6 py-16 text-center"><p className="font-semibold text-slate-800">{error}</p></Card>
      ) : rows && rows.length === 0 ? (
        <EmptyState
          icon={<Cloud className="h-6 w-6" />}
          title="No API endpoints yet"
          description="Design one from an OpenAPI schema, describe it to the AI, or expose a workflow — with Basic, client-credentials or JWT security."
          action={<Button onClick={() => setEditing("new")}><Plus className="h-4 w-4" /> New API endpoint</Button>}
        />
      ) : (
        <div className="space-y-3">
          {rows!.map((ep) => (
            <div
              key={ep.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/app/apis/${ep.slug}`)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/app/apis/${ep.slug}`)}
              className="flex w-full cursor-pointer items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-card transition hover:-translate-y-0.5 hover:shadow-lift"
            >
              <span className={cn("shrink-0 rounded-lg px-2.5 py-1 text-xs font-bold", methodTone[ep.method] ?? "bg-slate-100 text-slate-600")}>
                {ep.method}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-slate-900">{ep.name}</span>
                  <Badge tone={ep.mode === "mock" ? "violet" : "brand"}>{ep.mode}</Badge>
                  {!ep.enabled && <Badge tone="slate">Disabled</Badge>}
                </div>
                <code className="mt-0.5 block truncate text-xs text-slate-400">/api/gw/{tenant}/{ep.path}</code>
              </div>
              <div className="hidden items-center gap-3 text-xs text-slate-400 sm:flex">
                <span className="inline-flex items-center gap-1">
                  {ep.auth_type === "none" ? <Lock className="h-3.5 w-3.5 text-amber-500" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />}
                  {AUTH_LABELS[ep.auth_type]}
                </span>
                <span>{ep.call_count} call{ep.call_count === 1 ? "" : "s"}</span>
              </div>
              <div className="flex items-center gap-1">
                <span
                  onClick={(e) => { e.stopPropagation(); navigate(`/app/apis/${ep.slug}`); }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
                >
                  <Play className="h-3.5 w-3.5" /> Try it
                </span>
                <span
                  onClick={(e) => { e.stopPropagation(); setEditing(ep); }}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EndpointEditor
          tenant={tenant}
          existing={editing === "new" ? null : editing}
          workflows={workflows}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}
    </>
  );
}

type Mode = "workflow" | "mock";

function EndpointEditor({
  tenant,
  existing,
  workflows,
  onClose,
  onSaved,
}: {
  tenant: string;
  existing: ApiEndpoint | null;
  workflows: Workflow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [method, setMethod] = useState(existing?.method ?? "POST");
  const [path, setPath] = useState(existing?.path ?? "");
  const [mode, setMode] = useState<Mode>(existing?.mode ?? "mock");
  const [workflowSlug, setWorkflowSlug] = useState(existing?.workflow_slug ?? "");
  const [authType, setAuthType] = useState<AuthType>(existing?.auth_type ?? "none");
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  // auth
  const [username, setUsername] = useState(String(existing?.auth_config?.username ?? ""));
  const [password, setPassword] = useState("");
  const [issuer, setIssuer] = useState(String(existing?.auth_config?.issuer ?? ""));
  const [audience, setAudience] = useState(String(existing?.auth_config?.audience ?? ""));
  // schemas + mock as editable JSON text
  const [reqText, setReqText] = useState(pretty(existing?.request_schema));
  const [resText, setResText] = useState(pretty(existing?.response_schema));
  const [mockText, setMockText] = useState(pretty(existing?.mock_response));
  const [validateReq, setValidateReq] = useState(existing?.validate_request ?? false);
  // AI
  const [prompt, setPrompt] = useState("");
  const [designing, setDesigning] = useState(false);
  // preview
  const [preview, setPreview] = useState<{ status: number; body: unknown } | null>(null);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<ApiEndpointDetail | null>(null);

  const url = `${window.location.origin}/api/gw/${tenant}/${path || "…"}`;

  async function runDesign() {
    if (!prompt.trim()) return;
    setDesigning(true);
    setErr(null);
    try {
      const d = await designEndpoint(tenant, prompt);
      setName(d.name);
      setMethod(d.method);
      setPath(d.path.replace(/^\/+/, ""));
      setDescription(d.description);
      setReqText(pretty(d.request_schema));
      setResText(pretty(d.response_schema));
      setMockText(pretty(d.mock_response));
      setMode("mock");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "AI design failed.");
    } finally {
      setDesigning(false);
    }
  }

  async function runPreview() {
    const res = parseOrNull(resText);
    const mock = parseOrNull(mockText);
    if (res.error) return setErr(`Response schema: ${res.error}`);
    if (mock.error) return setErr(`Mock response: ${mock.error}`);
    setErr(null);
    try {
      setPreview(await mockPreview(tenant, { response_schema: res.value, mock_response: mock.value }));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Preview failed.");
    }
  }

  function auth(): Record<string, unknown> {
    if (authType === "basic") return { username, ...(password ? { password } : {}) };
    if (authType === "jwt") return { issuer, audience, rotate: !existing };
    if (authType === "client_credentials") return { rotate: !existing };
    return {};
  }

  async function save() {
    const req = parseOrNull(reqText);
    const res = parseOrNull(resText);
    const mock = parseOrNull(mockText);
    for (const [label, r] of [["Request schema", req], ["Response schema", res], ["Mock response", mock]] as const) {
      if (r.error) { setErr(`${label} is not valid JSON: ${r.error}`); return; }
    }
    setSaving(true);
    setErr(null);
    const body: ApiEndpointSave = {
      name, description, method, path, mode,
      workflow_slug: mode === "workflow" ? workflowSlug || null : null,
      auth_type: authType, auth: auth(),
      request_schema: req.value, response_schema: res.value, mock_response: mock.value,
      validate_request: validateReq, enabled,
    };
    try {
      const result = existing ? await updateEndpoint(tenant, existing.slug, body) : await createEndpoint(tenant, body);
      onSaved();
      if (Object.keys(result.reveal ?? {}).length > 0) setSaved(result);
      else onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't save the endpoint.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!existing || !confirm(`Delete “${existing.name}”? Callers will get 404 immediately.`)) return;
    await deleteEndpoint(tenant, existing.slug).catch(() => {});
    onSaved();
    onClose();
  }

  const curl = useMemo(() => buildCurl(method, url, authType, saved), [method, url, authType, saved]);

  if (saved) {
    return (
      <Modal open onClose={onClose} title="Endpoint saved — copy your credentials">
        <div className="space-y-4 px-6 py-5">
          <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0" /> These secrets are shown once. Store them now.
          </div>
          {Object.entries(saved.reveal).map(([k, v]) => (
            <div key={k}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{k.replace(/_/g, " ")}</p>
              <Copyable value={v} />
            </div>
          ))}
          {saved.token_url && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Token URL</p>
              <Copyable value={saved.token_url} />
            </div>
          )}
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Try it</p>
            <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs leading-relaxed text-slate-200">{curl}</pre>
          </div>
        </div>
        <div className="flex justify-end border-t border-slate-100 px-6 py-4"><Button onClick={onClose}>Done</Button></div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={existing ? "Edit API endpoint" : "Design an API endpoint"} className="max-w-3xl">
      <div className="max-h-[72vh] space-y-5 overflow-y-auto px-6 py-5">
        {err && (
          <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {err}
          </div>
        )}

        {/* AI design */}
        {!existing && (
          <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-brand-800">
              <Sparkles className="h-4 w-4" /> Design with AI
            </p>
            <p className="mt-1 text-xs text-slate-500">Describe the API; the schemas and a sample response are drafted for you.</p>
            <div className="mt-3 flex gap-2">
              <Input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runDesign()}
                placeholder="e.g. Create an order with a customer, items and total; return the order + status"
              />
              <Button onClick={runDesign} loading={designing} disabled={!prompt.trim()}>Design</Button>
            </div>
          </div>
        )}

        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Create order" /></Field>

        <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
          <Field label="Method">
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>{METHODS.map((m) => <option key={m}>{m}</option>)}</Select>
          </Field>
          <Field label="Path" hint="No leading slash.">
            <Input value={path} onChange={(e) => setPath(e.target.value.replace(/^\/+/, ""))} placeholder="orders" />
          </Field>
        </div>

        <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Endpoint URL</p>
          <code className="mt-1 block break-all text-sm text-slate-700">{method} {url}</code>
        </div>

        {/* Mode */}
        <div>
          <p className="mb-2 text-sm font-semibold text-slate-700">Behaviour</p>
          <div className="grid grid-cols-2 gap-2">
            <ModeChip active={mode === "mock"} onClick={() => setMode("mock")} title="Mock" desc="Return a designed/generated response" />
            <ModeChip active={mode === "workflow"} onClick={() => setMode("workflow")} title="Workflow" desc="Run a workflow, return its result" />
          </div>
          {mode === "workflow" && (
            <div className="mt-3">
              <Field label="Runs workflow">
                <Select value={workflowSlug} onChange={(e) => setWorkflowSlug(e.target.value)}>
                  <option value="">— none (echo the request) —</option>
                  {workflows.map((w) => <option key={w.slug} value={w.slug}>{w.name}</option>)}
                </Select>
              </Field>
            </div>
          )}
        </div>

        {/* Schemas */}
        <SchemaBox label="Request schema (OpenAPI / JSON Schema)" value={reqText} onChange={setReqText}
          hint="Validated on each call when 'validate requests' is on." />
        <label className="flex items-center gap-2.5 text-sm text-slate-600">
          <input type="checkbox" checked={validateReq} onChange={(e) => setValidateReq(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600" />
          Reject requests that don't match the request schema (400)
        </label>

        <SchemaBox label="Response schema" value={resText} onChange={setResText}
          hint="Drives the mock output and the OpenAPI document." />

        {mode === "mock" && (
          <div>
            <SchemaBox label="Mock response (optional {status, body})" value={mockText} onChange={setMockText}
              hint="Exact response to return. Leave blank to generate one from the response schema." />
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={runPreview}><Play className="h-3.5 w-3.5" /> Preview mock</Button>
              {preview && <span className="text-xs text-slate-400">→ HTTP {preview.status}</span>}
            </div>
            {preview && (
              <pre className="mt-2 max-h-40 overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-200">{JSON.stringify(preview.body, null, 2)}</pre>
            )}
          </div>
        )}

        <Field label="Description"><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>

        {/* Auth */}
        <div>
          <p className="mb-2 text-sm font-semibold text-slate-700">Authentication</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(Object.keys(AUTH_LABELS) as AuthType[]).map((t) => (
              <button key={t} type="button" onClick={() => setAuthType(t)}
                className={cn("focus-ring rounded-xl border px-2 py-2.5 text-xs font-semibold transition",
                  authType === t ? "border-brand-400 bg-brand-50 text-brand-700 ring-1 ring-brand-200" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300")}>
                {AUTH_LABELS[t]}
              </button>
            ))}
          </div>
          {authType === "basic" && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Username"><Input value={username} onChange={(e) => setUsername(e.target.value)} /></Field>
              <Field label={existing ? "New password (blank = keep)" : "Password"}>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </Field>
            </div>
          )}
          {authType === "client_credentials" && (
            <p className="mt-3 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">
              We issue a <strong>client ID + secret</strong>; callers exchange them at the token URL for a bearer token.{existing && " Saving rotates the secret."}
            </p>
          )}
          {authType === "jwt" && (
            <div className="mt-3 space-y-3">
              <p className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">
                We generate a <strong>shared HS256 secret</strong>; callers sign their own JWT with it.{existing && " Saving rotates the secret."}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Issuer (optional)"><Input value={issuer} onChange={(e) => setIssuer(e.target.value)} /></Field>
                <Field label="Audience (optional)"><Input value={audience} onChange={(e) => setAudience(e.target.value)} /></Field>
              </div>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2.5 text-sm text-slate-600">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600" />
          Enabled (callable)
        </label>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
        {existing ? <Button variant="ghost" onClick={remove} className="text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /> Delete</Button> : <span />}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving} disabled={!name || !path}><Save className="h-4 w-4" /> {existing ? "Save" : "Create"}</Button>
        </div>
      </div>
    </Modal>
  );
}

function ModeChip({ active, onClick, title, desc }: { active: boolean; onClick: () => void; title: string; desc: string }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("focus-ring rounded-xl border p-3 text-left transition",
        active ? "border-brand-400 bg-brand-50 ring-1 ring-brand-200" : "border-slate-200 bg-white hover:border-slate-300")}>
      <span className={cn("block text-sm font-semibold", active ? "text-brand-700" : "text-slate-700")}>{title}</span>
      <span className="mt-0.5 block text-xs text-slate-500">{desc}</span>
    </button>
  );
}

function SchemaBox({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      <Textarea
        rows={value.split("\n").length > 4 ? Math.min(value.split("\n").length, 14) : 4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="font-mono text-xs"
        placeholder={'{\n  "type": "object",\n  "properties": { }\n}'}
      />
    </Field>
  );
}

function buildCurl(method: string, url: string, authType: AuthType, saved: ApiEndpointDetail | null): string {
  const lines = [`curl -X ${method} ${url} \\`];
  if (authType === "basic") lines.push(`  -u '${saved?.auth_config?.username ?? "USER"}:PASSWORD' \\`);
  else if (authType === "client_credentials") lines.push(`  -H 'Authorization: Bearer $TOKEN' \\`);
  else if (authType === "jwt") lines.push(`  -H 'Authorization: Bearer $YOUR_SIGNED_JWT' \\`);
  lines.push(`  -H 'Content-Type: application/json' \\`, `  -d '{}'`);
  return lines.join("\n");
}
